// Thin wrapper over @workspace/messaging-ui's Composer (tdd-0050). Keeps the exact
// same props ChatWidget already passes. Chat's app-specific controls — history +
// memory buttons, the voice-input orb, and the mobile persona/settings row — are
// injected through the Composer's leftSlot / rightSlot / belowSlot. The generic
// textarea + attach/stop/send mechanics now live in the shared package.

import { useCallback, useEffect, useRef, type ReactNode } from "react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui";
// Direct, not via @workspace/ui/lib/icons: that module is `export * from
// 'lucide-react'`, and `optimizePackageImports` matches on the import specifier,
// so it cannot see through the workspace re-export and the ~1,500-module barrel
// gets walked at build time.
//
// Build-speed only. It does NOT shrink the shipped bundle — gen-ui's
// `import * as LucideIcons` retains the full icon set regardless. See tdd-0074.
import {
  Mic,
  MessagesSquare,
  Settings,
  Loader2,
  Paperclip,
} from "lucide-react";
import { cn, formatTokenCount } from "../../utils";
import { AddMemoryButton, REMEMBER_PREFIX, PrivateBadge } from "@workspace/ui/components/memory";
import { Composer } from "@workspace/messaging-ui";
import { PersonaSelector } from "./persona-selector";
import { PromptSuggestion } from "./prompt-suggestion";
import type { PromptSuggestionHandle } from "./prompt-suggestion";
import type { Persona } from "@workspace/personas";
import type { Model } from "../../types";

interface PromptInputProps {
  input: string;
  setInput: (value: string) => void;
  /**
   * Submit the composer. The optional second argument sends that text instead
   * of the current `input` — needed when setting text and submitting in one
   * action, since the handler closes over the pre-click value.
   */
  handleSubmit: (
    e?: { preventDefault?: () => void },
    overrideInput?: string,
  ) => void;
  isLoading: boolean;
  stop: () => void;
  /**
   * Opens a file picker. `mode` decides WHICH picker on mobile — "images"
   * surfaces the photo library, "files" opens Files / iCloud Drive.
   */
  onAttachFile?: (opts?: { mode?: "images" | "files" | "any" }) => void;
  onVoiceInput?: () => void;
  onOpenHistory?: () => void;
  /**
   * Context management, moved down from the desktop header (which no longer
   * exists). The token count doubles as the button: it is the only place the
   * usage-vs-limit number appears, so hiding it behind a menu would cost the
   * at-a-glance read the header gave for free.
   */
  onDeleteMessages?: () => void;
  messageCount?: number;
  currentTokens?: number;
  maxContextTokens?: number;
  isVoiceActive?: boolean;
  /**
   * Voice backend reachable but not yet able to serve audio — s2s is lazy-started
   * and loads its speech models after binding the port, so there's a real gap
   * between "clicked" and "listening". Shows a spinner so the wait reads as
   * progress rather than a dead button.
   */
  isVoiceLoading?: boolean;
  enableFileUpload?: boolean;
  enableVoice?: boolean;
  enableMemory?: boolean;
  hasFiles?: boolean;
  /**
   * Staged attachments, rendered in the gap between the two control groups.
   *
   * Passed as a node rather than the file list so this component stays free of
   * attachment concerns — it decides WHERE the indicator sits, ChatWidget
   * decides what it says.
   */
  attachmentsSlot?: ReactNode;
  /**
   * Mobile controls — the header is hidden on mobile, so the model selector +
   * settings live in a compact row below the input instead.
   */
  onSettings?: () => void;
  personas?: Persona[];
  selectedPersonaId?: string | null;
  onPersonaChange?: (persona: Persona) => void;
  onCreatePersona?: () => void;
  onEditPersona?: (persona: Persona) => void;
  onDeletePersona?: (personaId: string) => void;
  models?: Model[];
  modelsLoading?: boolean;
  modelsError?: string | null;
  /** Active thread is private — shows the "Private" badge in the mobile row. */
  isPrivate?: boolean;
  onShowPrivateInfo?: () => void;
  hasMessages?: boolean;
  /**
   * Example prompts cycled over the empty input as a decorative pseudo-label.
   * Empty (the default) renders nothing — the textarea's native placeholder
   * still applies.
   */
  promptSuggestions?: string[];
  /**
   * The user's own messages, most recent LAST, for ↑ recall.
   *
   * Text only — recall re-populates the composer, and an attachment cannot be
   * put back in it. Passing the messages rather than a callback keeps the
   * cursor into the history here, next to the input it drives.
   */
  recallHistory?: string[];
}

const defaultPromptSuggestions = [
  "What are today's headlines?",
  "Help me edit a photo",
  "Suggest some things to do near me",
];

export function PromptInput({
  input,
  setInput,
  handleSubmit,
  isLoading,
  stop,
  onAttachFile,
  onVoiceInput,
  onOpenHistory,
  onDeleteMessages,
  messageCount = 0,
  currentTokens = 0,
  maxContextTokens = 0,
  isVoiceActive = false,
  isVoiceLoading = false,
  enableFileUpload = true,
  enableVoice = true,
  enableMemory = false,
  hasFiles = false,
  attachmentsSlot,
  onSettings,
  personas,
  selectedPersonaId,
  onPersonaChange,
  onCreatePersona,
  onEditPersona,
  onDeletePersona,
  models,
  modelsLoading = false,
  modelsError = null,
  isPrivate = false,
  onShowPrivateInfo,
  hasMessages = false,
  promptSuggestions = defaultPromptSuggestions,
  recallHistory,
}: PromptInputProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  /**
   * Shell-style ↑ history.
   *
   * The cursor counts BACK from the newest message (0 = nothing recalled), and
   * resets whenever the history changes — a newly sent message means the user
   * has moved on, so the next ↑ should offer that one rather than resuming a
   * walk from an earlier session.
   *
   * A ref, not state: nothing renders from it, and making it state would
   * re-render the composer on every step for no visible reason.
   */
  const recallIndexRef = useRef(0);
  useEffect(() => {
    recallIndexRef.current = 0;
  }, [recallHistory]);

  // Back to an empty box = the walk is over, wherever it got to. Covers both
  // sending (which clears the input) and the user deleting it by hand; without
  // this a later ↑ resumed from the middle of the previous walk, which reads as
  // the history having skipped entries.
  useEffect(() => {
    if (!input) recallIndexRef.current = 0;
  }, [input]);

  const handleRecallPrevious = useCallback(() => {
    const history = recallHistory;
    if (!history?.length) return null;
    // Already at the oldest — stay there rather than wrapping around, which
    // would silently jump the user back to the newest message.
    if (recallIndexRef.current >= history.length) return null;
    recallIndexRef.current += 1;
    return history[history.length - recallIndexRef.current] ?? null;
  }, [recallHistory]);

  // Send the suggestion the user clicked, passing the text explicitly.
  //
  // `setInput(text)` followed by a bare `handleSubmit()` does NOT work: the
  // callback closes over `input` from the render that created it, so it reads
  // the pre-click (empty) value. Observed live — the message did send, but the
  // text stayed in the textarea afterwards, because the submit's own
  // `setInput("")` raced a later render that restored it. The override
  // parameter sidesteps the closure entirely.
  const handleSuggestionSubmit = (text: string) => {
    setInput(text);
    handleSubmit(undefined, text);
  };

  /**
   * Keyboard control of the suggestion carousel.
   *
   * Only while suggestions are actually on screen — empty input, no messages,
   * more than one suggestion. Outside that the keys must behave normally: ↑/↓
   * move the caret through a multi-line draft, and Enter sends.
   *
   * `interceptKeyDown` returning true tells the Composer the app consumed the
   * event, so Enter here sends the SUGGESTION rather than the empty textarea.
   */
  const suggestionRef = useRef<PromptSuggestionHandle>(null);
  const suggestionsActive =
    !input && !hasMessages && promptSuggestions.length > 0;

  const interceptKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (!suggestionsActive) return false;

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      // Only meaningful with somewhere to move; a single suggestion should
      // leave the arrows alone.
      if (promptSuggestions.length < 2) return false;
      e.preventDefault();
      suggestionRef.current?.step(e.key === "ArrowDown" ? 1 : -1);
      return true;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      const text = suggestionRef.current?.current();
      if (!text) return false;
      e.preventDefault();
      handleSuggestionSubmit(text);
      return true;
    }

    return false;
  };

  // Prefix the input with "remember: " (keeping existing content) so the message
  // is captured as a memory on send. Focuses the textarea afterwards.
  const handleAddMemory = () => {
    if (!input.startsWith(REMEMBER_PREFIX)) {
      setInput(`${REMEMBER_PREFIX}${input}`);
    }
    textareaRef.current?.focus();
  };

  /**
   * The composer's flight between the hero (centred) and docked (bottom)
   * positions.
   *
   * Both states use ONE positioning model — `left:50%` with a constant
   * `translateX(-50%)`, animating only `bottom`, `width` and `translateY`.
   * They used to differ: hero was `left-[50%] w-[54rem] translate(-50%,50%)`
   * while docked fell back to the base `left-0 right-0` with `translate(0,0)`
   * and an auto width. Three things went wrong at once:
   *
   *   - `width` animated from 54rem to `auto`, which is not an animatable
   *     value, so it snapped instead of easing.
   *   - `left` eased 50%→0 while `translateX` unwound -50%→0 at the same time.
   *     Two independent easings on the same axis do not cancel, so the element
   *     drifted sideways at a rate that matched neither.
   *   - `max-w-[100%]` silently clamped the 54rem start on narrower viewports,
   *     so the animation began somewhere other than where the class said.
   *
   * Now `left` and `translateX` are constant, and `width` runs between two
   * real lengths.
   */
  const rootClassName = hasMessages
    ? "bottom-[0%] w-full transform-[translate(-50%,0%)] left-[50%]"
    : "bottom-[50%] w-[min(54rem,100%)] transform-[translate(-50%,50%)] left-[50%]";

  // The send button belongs to the shared Composer, so it cannot take a
  // wrapper here. Targeted from the root instead — dimming it in step with
  // everything else, without pushing a chat-specific visual state into
  // @workspace/messaging-ui.
  //
  // Focus is keyed off `data-focused`, which Composer sets on this same root.
  // Two things happen together, and the pairing is the point: the input itself
  // gains a soft primary glow, and the control row beside it comes up to full
  // strength. The row is otherwise dimmed to 40% on the hero screen (see
  // `leftSlot`), so focusing the box is also what reveals what you can do with
  // it — rather than the controls being permanently faded or permanently loud.
  const composerClassName = cn(
    "relative",
    "[&_button[aria-label=Send]]:transition-opacity [&_button[aria-label=Send]]:duration-[1s]",
    "motion-reduce:[&_button[aria-label=Send]]:transition-none",
    hasMessages
      ? "[&_button[aria-label=Send]]:opacity-100"
      : "[&_button[aria-label=Send]]:opacity-40",
    // Full-strength controls while focused, whichever state the composer is in.
    "data-[focused]:[&_[data-composer-controls]>*]:opacity-100",
  );
  const textareaClassName = cn(
    "transition-[padding,height,width,border-radius,box-shadow,border-color] backdrop-blur",
    // Focus styling is deliberately NOT overridden here: the Textarea's own
    // `focus-visible:ring-2 ring-ring ring-offset-2` is what every other input
    // in the product uses, and a composer that focuses differently from the
    // rest reads as a different kind of control. The softer custom glow that
    // used to live here is gone.
    !hasMessages &&
      "p-[1.5rem] h-[calc(4.5rem+2px)] min-h-0 max-h-[none] max-w-[100%] rounded-[4.5rem]",
  );

  /**
   * The composer chrome's DECORATION, kept apart from any component's base.
   *
   * Every control here sits over the conversation rather than a solid bar, so
   * each needs its own backdrop to stay legible against whatever has scrolled
   * underneath it — that is the whole of what this says. Size, shape, colour
   * and disabled state belong to the components (Composer, PersonaSelector,
   * AddMemoryButton), which is why none of that is repeated here: passing the
   * base back in emitted every utility twice.
   *
   * Applied LAST everywhere it is used, so it can also override a base utility
   * — `rounded-full` deliberately beats PersonaSelector's default `rounded-md`
   * so the whole row reads as one set of controls.
   */
  /**
   * Decoration for each END of the toolbar row, handed to Composer.
   *
   * The backdrop blur belongs to the GROUP, not the buttons. When every control
   * carried its own, each sampled the backdrop independently — so adjacent
   * buttons double-blurred where they met and the row read as separate discs
   * floating over the conversation rather than one bar of controls. Send and
   * the mic showed it worst, since they sit next to each other.
   */
  const controlGroup = "rounded-full backdrop-blur";

  const controlDecoration = cn(
    "rounded-full",
    // A slight darkened plate on hover, in ADDITION to whatever each control
    // does to its glyph (most brighten `text-muted-foreground` to
    // `text-foreground`). `foreground/10` rather than a fixed colour so it
    // darkens on a light theme and lightens on a dark one — a hardcoded black
    // would vanish against a dark backdrop.
    //
    // `transition-colors` is on the shared button base already; naming it here
    // too would emit the utility twice.
    "hover:bg-background/10",
  );

  /**
   * The full class for a button chat renders ITSELF (settings, history,
   * attach). Buttons owned by a package get `controlDecoration` alone — the
   * package already supplies this base.
   */
  const buttonClassName = cn(
    "inline-flex items-center justify-center h-9 w-9",
    "text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors",
    controlDecoration,
  );

  // Context usage colouring, carried over from the header verbatim: the
  // thresholds are what users already read this number against.
  const contextPercentage =
    maxContextTokens > 0
      ? Math.min(100, Math.round((currentTokens / maxContextTokens) * 100))
      : 0;
  const contextColor =
    contextPercentage >= 90
      ? "text-red-500"
      : contextPercentage >= 80
        ? "text-orange-500"
        : contextPercentage >= 60
          ? "text-amber-500"
          : "text-muted-foreground";

  // Everything lives in ONE row now: conversations, settings, model, context
  // and the private badge on the left; voice and send on the right. This used
  // to be split across two rows, which spent vertical space on a handful of
  // icons and read as two unrelated groups of controls.
  const personaControl = onPersonaChange && onCreatePersona && models && (
    <PersonaSelector
      variant="minimal"
      personas={personas ?? []}
      triggerClass={controlDecoration}
      selectedPersonaId={selectedPersonaId ?? null}
      onPersonaChange={onPersonaChange}
      onCreateNew={onCreatePersona}
      onEditPersona={onEditPersona}
      onDeletePersona={onDeletePersona}
      models={models}
      isLoading={modelsLoading}
      error={modelsError}
      disabled={isLoading}
    />
  );

  const settingsControl = onSettings && (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          type="button"
          onClick={onSettings}
          aria-label="Settings"
          className={buttonClassName}
        >
          <Settings className="h-4 w-4 md:h-5 md:w-5" />
        </TooltipTrigger>
        <TooltipContent>Settings</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  // Text rather than an icon: the number IS the information, and it is the
  // only place usage-vs-limit appears.
  const contextControl = messageCount > 0 && onDeleteMessages && (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          type="button"
          onClick={onDeleteMessages}
          disabled={isLoading}
          // Text, not an icon button — it takes the decoration but not the
          // fixed 36px square base.
          className={cn(
            "text-xs px-1.5 py-1 hover:underline transition-colors disabled:opacity-50",
            controlDecoration,
            contextColor,
          )}
        >
          {formatTokenCount(currentTokens)}
          {maxContextTokens > 0 && (
            <>
              {" "}
              / {formatTokenCount(maxContextTokens)} ({contextPercentage}%)
            </>
          )}
        </TooltipTrigger>
        <TooltipContent>
          {messageCount} {messageCount === 1 ? "message" : "messages"} — click
          to manage context
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const leftSlot = (
    // Dimmed on the empty ("hero") state, full strength once the composer has
    // flown to the bottom.
    <div
      // Marked so the composer root can bring these to full strength on focus
      // (see `composerClassName`). `contents` means this element generates no
      // box of its own, so the attribute is a hook and nothing else.
      data-composer-controls=""
      className={cn(
        // `contents` again: the blurred group box is Composer's now (see
        // `controlGroupClassName`), which is what lets send and stop share one
        // backdrop with the mic instead of stacking their own beside it.
        "contents [&>*]:transition-opacity [&>*]:duration-[1s]",
        "motion-reduce:[&>*]:transition-none",
        hasMessages ? "[&>*]:opacity-100" : "[&>*]:opacity-40",
      )}
    >
      {onOpenHistory && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              type="button"
              onClick={onOpenHistory}
              // Icon-only, and a TooltipContent is not an accessible name —
              // every sibling in this row carries one, this was the exception.
              aria-label="Other chat sessions"
              className={buttonClassName}
            >
              <MessagesSquare className="h-4 w-4 md:h-5 md:w-5" />
            </TooltipTrigger>
            <TooltipContent>Other chat sessions</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {settingsControl}
      {personaControl}
      {contextControl}
      {enableFileUpload && onAttachFile && (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              type="button"
              onClick={() => onAttachFile({ mode: "any" })}
              // Deliberately NOT disabled while generating. Attaching is part of
              // composing the *next* message, and a user shouldn't have to wait
              // for the model to finish before picking a file — uploads run
              // independently, and Composer still blocks the actual send.
              aria-label="Attach a photo or file"
              className={buttonClassName}
            >
              <Paperclip className="h-4 w-4 md:h-5 md:w-5" />
            </TooltipTrigger>
            <TooltipContent>Attach a photo or file</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      {isPrivate && (
        <PrivateBadge
          onClick={onShowPrivateInfo}
          className={controlDecoration}
        />
      )}

      {enableMemory && (
        <AddMemoryButton
          onClick={handleAddMemory}
          className={controlDecoration}
        />
      )}
    </div>
  );

  // Three visual states, deliberately distinct at a glance:
  //   idle     — mic glyph, borderless, matches the other composer controls
  //   loading  — mic stays put, a spinner ring orbits it; NOT yet listening
  //   active   — red stop square + pulsing red ring
  // `loading` is checked first: until the voice session is live, showing the
  // stop affordance would claim a capability we don't have yet.
  // Same dimming as leftSlot. The mic lives in `rightSlot`, which sits outside
  // that wrapper, so it stayed at full strength while everything beside it
  // faded — the one icon that looked deliberately emphasised when nothing
  // about it is.
  const rightSlot =
    /* keep ability to swap to voice mode even if message has been entered 
    !input && */
    enableVoice && onVoiceInput ? (
      <div
        className={cn(
          "contents [&>*]:transition-opacity [&>*]:duration-[1s]",
          "motion-reduce:[&>*]:transition-none",
          hasMessages ? "[&>*]:opacity-100" : "[&>*]:opacity-40",
        )}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger
              type="button"
              onClick={onVoiceInput}
              disabled={isLoading}
              aria-label={
                isVoiceLoading
                  ? "Starting voice input"
                  : isVoiceActive
                    ? "Stop recording"
                    : "Voice input"
              }
              aria-busy={isVoiceLoading || undefined}
              className={cn(
                buttonClassName,
                // `relative` positions the loading spinner and the active ring
                // against the button; the rest of the base comes from
                // `buttonClassName` rather than being restated.
                "relative",
                isVoiceActive && "text-red-500 hover:text-red-400",
              )}
            >
              {isVoiceActive ? (
                // Rounded square = the universal "stop" affordance. Drawn as a
                // filled div rather than an icon so the corner radius is ours.
                <span className="h-3 w-3 md:h-3.5 md:w-3.5 rounded-[3px] bg-current" />
              ) : (
                <Mic className="h-4 w-4 md:h-5 md:w-5" />
              )}

              {/* Loading: spinner orbits the mic. Inset so the ring sits just
                outside the glyph rather than cropping it. */}
              {isVoiceLoading && !isVoiceActive && (
                <Loader2 className="absolute inset-0 h-full w-full animate-spin text-muted-foreground/60 pointer-events-none" />
              )}

              {/* Active: pulsing red ring. `animate-ping` is a one-shot-looking
                expand+fade, which reads as a live indicator; the static ring
                underneath keeps the boundary legible between pulses. */}
              {isVoiceActive && (
                <>
                  <span className="absolute inset-0 rounded-full border border-red-500/70 pointer-events-none" />
                  {/* <span className="absolute inset-0 rounded-full border border-red-500 animate-ping pointer-events-none" /> */}
                </>
              )}
            </TooltipTrigger>
            <TooltipContent>
              {isVoiceLoading
                ? "Starting voice service…"
                : isVoiceActive
                  ? "Stop recording"
                  : "Voice input"}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ) : null;

  return (
    <div
      className={cn(
        "fixed z-9",
        "transition-[bottom,width,transform] duration-[1s]",
        "motion-reduce:transition-none",
        rootClassName,
      )}
    >
      <div className="mx-auto w-full max-w-[75rem] py-4 px-3 xl:px-0">
        <Composer
          className={composerClassName}
          // Decoration only: Composer owns the base (size, shape, colour).
          buttonClassName={controlDecoration}
          controlGroupClassName={controlGroup}
          gapSlot={attachmentsSlot}
          input={input}
          setInput={setInput}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          onStop={stop}
          // Deliberately not passed: chat renders its own attach control in
          // `leftSlot` so it can offer a photo/file choice. Passing this too
          // would render the Composer's plain paperclip alongside it.
          // onAttachFile={undefined}
          hasFiles={hasFiles}
          textareaRef={textareaRef}
          textareaClassName={textareaClassName}
          // Hero state is a fixed single-line pill, so the auto-grow's inline
          // height — which beats any class — has to be off for `h-[...]` to
          // hold. Once the conversation starts the textarea grows normally.
          disableAutoGrow={!hasMessages}
          interceptKeyDown={interceptKeyDown}
          // Escape is free here: the composer has no popover of its own to
          // dismiss, so the key is otherwise unused while typing.
          stopOnEscape
          onRecallPrevious={handleRecallPrevious}
          leftSlot={leftSlot}
          rightSlot={rightSlot}
          overlaySlot={
            promptSuggestions.length > 0 && (
              <PromptSuggestion
                ref={suggestionRef}
                texts={promptSuggestions}
                // Only ever shown on an empty input. Once there's text the real
                // content occupies the same space, and once the user has a
                // conversation going the nudge has served its purpose.
                hidden={!!input || hasMessages}
                className={hasMessages ? undefined : "items-center px-6"}
                onSubmit={handleSuggestionSubmit}
              />
            )
          }
        />
      </div>
    </div>
  );
}
